import React, { useState, useEffect } from 'react';
import { X, Copy, Check } from 'lucide-react';
import Button from './Button';
import Input from './Input';
import Card from './Card';
import { invitesApi } from '../lib/api/invites';

interface AdminInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminInviteModal: React.FC<AdminInviteModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPhone('');
      setExpiresInDays(30);
      setError('');
      setInviteUrl('');
      setCopied(false);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email && !phone) {
      setError('Please provide either an email or phone number');
      return;
    }

    setLoading(true);

    try {
      const payload: {
        type: 'club_leader';
        club_id: string;
        invitee_email?: string;
        invitee_phone?: string;
        expires_in_days?: number;
      } = {
        type: 'club_leader',
        club_id: 'none', // Club leader invites create new clubs
        expires_in_days: expiresInDays
      };

      if (email) payload.invitee_email = email;
      if (phone) payload.invitee_phone = phone;

      const response = await invitesApi.createClubLeaderInvite(payload);
      setInviteUrl(response.invite.invite_url);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy link');
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-invite-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm oro-fade-in"
        onClick={handleClose}
        aria-label="Close modal"
      />
      
      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-md oro-modal-enter">
        <Card className="border-accent/20">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 id="admin-invite-title" className="text-xl font-bold text-text-primary">
              Create Club Leader Invite
            </h2>
            <button
              onClick={handleClose}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-surface-alt"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Success State: Show Invite Link */}
          {inviteUrl ? (
            <div className="space-y-4">
              <div className="p-4 bg-accent/10 border border-accent/30 rounded-lg">
                <p className="text-sm text-text-secondary mb-2">
                  Invite created successfully! Share this link:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-surface-alt rounded text-xs text-accent break-all">
                    {inviteUrl}
                  </code>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleCopyLink}
                    className="shrink-0"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="p-3 bg-surface-alt rounded-lg">
                <p className="text-xs text-text-muted">
                  This invite will allow the recipient to create a club and become a club leader.
                  It expires in {expiresInDays} days.
                </p>
              </div>

              <Button
                variant="primary"
                onClick={handleClose}
                className="w-full"
              >
                Done
              </Button>
            </div>
          ) : (
            /* Form State: Create Invite */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-3 bg-surface-alt rounded-lg mb-4">
                <p className="text-xs text-text-muted">
                  Create an invite for someone to become a club leader. They'll be able to create and manage their own club.
                </p>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="leader@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* OR Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-text-muted">OR</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Phone Input */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-text-secondary mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-text-muted mt-1">
                  Include country code for international numbers
                </p>
              </div>

              {/* Expiration */}
              <div>
                <label htmlFor="expires" className="block text-sm font-medium text-text-secondary mb-2">
                  Expires In (Days)
                </label>
                <Input
                  id="expires"
                  type="number"
                  min="1"
                  max="365"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 30)}
                  disabled={loading}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  className="flex-1"
                >
                  Create Invite
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminInviteModal;
